import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import RefreshIcon from '@mui/icons-material/Refresh'
import StoreIcon from '@mui/icons-material/Store'
import useAuthStore from '../store/authStore'
import { vendorService } from '../services'
import { getApiErrorMessage } from '../services/apiClient'
import { formatDateTime } from '../utils/dateFormat'
import { parseVendorFileBuffer, validateVendorRecord } from '../utils/vendorExcelValidation'
import ManagementSearchBar from '../components/shared/ManagementSearchBar'
import TableEmptyState from '../components/shared/TableEmptyState'
import TableLoadingRow from '../components/shared/TableLoadingRow'

const emptyForm = { vendor_id: '', vendor_name: '' }

const VendorManagementPage = () => {
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const fileInputRef = useRef(null)

  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [listError, setListError] = useState(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [formError, setFormError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const [uploadFile, setUploadFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [previewing, setPreviewing] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [snackbar, setSnackbar] = useState(null)

  const applySearchQuery = useCallback((query) => {
    setDebouncedSearch(query)
    setPage(0)
  }, [])

  const loadVendors = useCallback(async () => {
    setLoading(true)
    setListError(null)
    try {
      const params = { page: page + 1, limit: rowsPerPage }
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim()
      const data = await vendorService.list(params)
      setVendors(data?.items || [])
      setTotalItems(data?.pagination?.total || 0)
    } catch (error) {
      setVendors([])
      setTotalItems(0)
      setListError(getApiErrorMessage(error, 'Failed to load vendors'))
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, debouncedSearch])

  useEffect(() => {
    if (!isSuperuserActor) return
    loadVendors()
  }, [isSuperuserActor, loadVendors])

  const openCreate = () => {
    setEditingId(null)
    setForm(emptyForm)
    setFormError(null)
    setDialogOpen(true)
  }

  const openEdit = (vendor) => {
    setEditingId(vendor.vendor_id)
    setForm({
      vendor_id: vendor.vendor_id || '',
      vendor_name: vendor.vendor_name || '',
    })
    setFormError(null)
    setDialogOpen(true)
  }

  const closeDialog = () => {
    if (saving) return
    setDialogOpen(false)
    setFormError(null)
  }

  const handleSave = async () => {
    const result = validateVendorRecord(form.vendor_id, form.vendor_name)
    if (!result.ok) {
      setFormError(result.errors.join(' '))
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      if (editingId) {
        await vendorService.update(editingId, result.vendor)
        setSnackbar({ severity: 'success', message: 'Vendor updated' })
      } else {
        await vendorService.create(result.vendor)
        setSnackbar({ severity: 'success', message: 'Vendor created' })
      }
      setDialogOpen(false)
      loadVendors()
    } catch (error) {
      setFormError(getApiErrorMessage(error, 'Could not save vendor'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget?.vendor_id) return
    setDeleting(true)
    try {
      await vendorService.remove(deleteTarget.vendor_id)
      setSnackbar({ severity: 'success', message: 'Vendor removed' })
      setDeleteTarget(null)
      if (vendors.length === 1 && page > 0) {
        setPage(page - 1)
      } else {
        loadVendors()
      }
    } catch (error) {
      setSnackbar({
        severity: 'error',
        message: getApiErrorMessage(error, 'Could not delete vendor'),
      })
    } finally {
      setDeleting(false)
    }
  }

  const clearUpload = () => {
    setUploadFile(null)
    setPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFile = async (file) => {
    if (!file) {
      clearUpload()
      return
    }
    const name = file.name.toLowerCase()
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      setUploadFile(null)
      setPreview({
        fatal: 'Upload an Excel file (.xlsx or .xls)',
        errors: [],
        validRows: [],
      })
      return
    }
    setUploadFile(file)
    setPreviewing(true)
    if (fileInputRef.current) fileInputRef.current.value = ''
    try {
      const buffer = await file.arrayBuffer()
      setPreview(parseVendorFileBuffer(buffer))
    } catch {
      setPreview({
        fatal: 'Could not read this Excel file. Use the vendor template.',
        errors: [],
        validRows: [],
      })
    } finally {
      setPreviewing(false)
    }
  }

  const handleImport = async () => {
    if (!uploadFile || preview?.fatal || preview?.errors?.length || !preview?.validRows?.length) {
      return
    }
    setUploading(true)
    try {
      const result = await vendorService.importFile(uploadFile)
      const created = Number(result?.created) || 0
      const updated = Number(result?.updated) || 0
      setSnackbar({
        severity: 'success',
        message: `Imported vendors. Created ${created}, updated ${updated}.`,
      })
      clearUpload()
      loadVendors()
    } catch (error) {
      const apiErrors = error?.response?.data?.errors
      if (Array.isArray(apiErrors) && apiErrors.length) {
        setPreview((current) => ({
          fatal: null,
          validRows: current?.validRows || [],
          errors: apiErrors,
        }))
      }
      setSnackbar({
        severity: 'error',
        message: getApiErrorMessage(error, 'Vendor file was not imported'),
      })
    } finally {
      setUploading(false)
    }
  }

  if (!isSuperuserActor) {
    return <Navigate to="/dashboard" replace />
  }

  const uploadBlocked =
    previewing ||
    uploading ||
    !uploadFile ||
    Boolean(preview?.fatal) ||
    (preview?.errors?.length || 0) > 0 ||
    (preview?.validRows?.length || 0) === 0

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <StoreIcon sx={{ color: '#e90000', fontSize: 32, mt: 0.5 }} />
        <Box sx={{ flexGrow: 1, minWidth: 240 }}>
          <Typography variant="h5" fontWeight={600}>
            Vendor Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Vendor ID is the unique key. Add or edit a vendor here, or upload Excel with Vendor
            Name and Vendor ID columns. An existing Vendor ID updates that name. A new Vendor ID
            creates a vendor. Duplicate or invalid rows reject the whole file.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add vendor
        </Button>
        <Tooltip title="Refresh">
          <IconButton onClick={loadVendors} aria-label="Refresh vendors">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          Excel upload
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The first row must be the headers Vendor Name and Vendor ID. Format Vendor ID as text so
          leading zeros are kept. Nothing is saved until every row is valid.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={async () => {
              try {
                await vendorService.downloadTemplate()
              } catch (error) {
                setSnackbar({
                  severity: 'error',
                  message: getApiErrorMessage(error, 'Could not download the template'),
                })
              }
            }}
          >
            Download template
          </Button>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={previewing || uploading}
          >
            Choose Excel file
            <input
              ref={fileInputRef}
              hidden
              type="file"
              accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(event) => handleFile(event.target.files?.[0] || null)}
            />
          </Button>
          <Button variant="contained" disabled={uploadBlocked} onClick={handleImport}>
            {uploading ? <CircularProgress size={22} color="inherit" /> : 'Upload vendors'}
          </Button>
        </Box>

        {previewing && <CircularProgress size={22} />}
        {uploadFile && !previewing && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            {uploadFile.name}
            {preview && !preview.fatal && preview.errors.length === 0
              ? ` — ${preview.validRows.length} vendor${preview.validRows.length === 1 ? '' : 's'} ready`
              : ''}
          </Typography>
        )}
        {preview?.fatal && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {preview.fatal}
          </Alert>
        )}
        {preview?.errors?.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="error" sx={{ mb: 1 }}>
              {preview.errors.length} row{preview.errors.length === 1 ? '' : 's'} need to be fixed.
              Nothing is saved until the file is valid.
            </Alert>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Vendor ID</TableCell>
                    <TableCell>Vendor Name</TableCell>
                    <TableCell>Problem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.errors.map((error) => (
                    <TableRow key={`${error.row}-${error.vendor_id}-${error.message}`}>
                      <TableCell>{error.row}</TableCell>
                      <TableCell>{error.vendor_id || '—'}</TableCell>
                      <TableCell>{error.vendor_name || '—'}</TableCell>
                      <TableCell>{error.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {listError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setListError(null)}>
          {listError}
        </Alert>
      )}

      <ManagementSearchBar
        label="Search by vendor name or vendor ID"
        placeholder="Search vendors..."
        onApplySearchQuery={applySearchQuery}
      />

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8f9fb' }}>
              <TableCell sx={{ fontWeight: 600 }}>Vendor ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Vendor Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created by</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Updated by</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableLoadingRow colSpan={6} />
            ) : vendors.length === 0 ? (
              <TableEmptyState colSpan={6} message="No vendors found" />
            ) : (
              vendors.map((vendor) => (
                <TableRow key={vendor.vendor_id} hover>
                  <TableCell>{vendor.vendor_id}</TableCell>
                  <TableCell>{vendor.vendor_name}</TableCell>
                  <TableCell>{vendor.created_by || '—'}</TableCell>
                  <TableCell>{vendor.updated_by || '—'}</TableCell>
                  <TableCell>{formatDateTime(vendor.updated_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit vendor">
                      <IconButton size="small" onClick={() => openEdit(vendor)} aria-label="Edit vendor">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete vendor">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(vendor)}
                        aria-label="Delete vendor"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalItems}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10))
          setPage(0)
        }}
        rowsPerPageOptions={[10, 25, 50]}
      />

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit vendor' : 'Add vendor'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Vendor Name"
            fullWidth
            value={form.vendor_name}
            onChange={(event) => setForm((current) => ({ ...current, vendor_name: event.target.value }))}
          />
          <TextField
            margin="dense"
            label="Vendor ID"
            fullWidth
            value={form.vendor_id}
            onChange={(event) => setForm((current) => ({ ...current, vendor_id: event.target.value }))}
            helperText="Primary key. Letters, numbers, dots, hyphens, and underscores. Must be unique."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) setDeleteTarget(null)
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete vendor</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove {deleteTarget?.vendor_name || 'this vendor'} ({deleteTarget?.vendor_id}) from
            the list? The record is kept. Adding the same Vendor ID again restores it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={22} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity || 'success'} onClose={() => setSnackbar(null)} variant="filled">
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default VendorManagementPage
